import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, FileText, Download, BookOpen, Check } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../PageHeader';
import { documents, knowledgeArticles, DocItem } from '../data';
const typeStyles: Record<string, string> = {
  PDF: 'bg-clay/10 text-clay',
  DOCX: 'bg-sky/10 text-sky',
  XLSX: 'bg-sage/10 text-sage',
  Policy: 'bg-navy-100 text-navy-700',
  Guide: 'bg-gold-100 text-gold-700'
};
export function LibraryPage({
  kind
}: {
  kind: 'knowledge' | 'documents';
}) {
  const isKnowledge = kind === 'knowledge';
  const source: DocItem[] = isKnowledge ? knowledgeArticles : documents;
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('All');
  const [downloading, setDownloading] = useState<string | null>(null);
  const categories = ['All', ...Array.from(new Set(source.map(d => d.category)))];
  const filtered = source.filter(d => {
    const matchesQuery = d.title.toLowerCase().includes(query.toLowerCase());
    const matchesCat = cat === 'All' || d.category === cat;
    return matchesQuery && matchesCat;
  });
  const handleDownload = (d: DocItem) => {
    setDownloading(d.id);
    if (isKnowledge) {
      toast(`Opening "${d.title}"...`);
    } else {
      toast(`Downloading "${d.title}"...`);
    }
    setTimeout(() => setDownloading(null), 1500);
  };
  return <>
      <PageHeader title={isKnowledge ? 'Knowledge' : 'Documents'} subtitle={isKnowledge ? 'How-to guides and answers for getting work done.' : 'Policies, templates, and official files.'} data-id="element-499" data-mp="18oz82pzxjck8" />

      <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-500" data-mp="1dhaabzi57afy">
        <div className="relative flex-1 max-w-md" data-id="element-501" data-mp="1dhsctdveekwb">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" data-id="element-502" data-mp="1sdrh0dfe00w6" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder={isKnowledge ? 'Search guides…' : 'Search documents…'} className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white border border-navy-100 focus:border-gold-400 focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900" data-id="element-503" data-mp="1nfq121d9chox" />
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-504" data-mp="297spyiqts2vs">
          {categories.map(c => <button key={c} onClick={() => setCat(c)} className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors ${cat === c ? 'bg-navy-600 text-white' : 'bg-white text-navy-600 border border-navy-100 hover:border-gold-400'}`} data-id="element-505" data-mp="2e6drclhtcslt">
              {c}
            </button>)}
        </div>
      </div>

      {filtered.length === 0 ? <div className="text-center py-16 text-navy-400" data-id="element-506" data-mp="stkepkbi9fzg">
          Nothing matches your search.
        </div> : <div className="bg-white rounded-xl border border-navy-100 divide-y divide-navy-50 overflow-hidden" data-id="element-507" data-mp="xt6rxtrc2w89">
          {filtered.map((d, i) => {
        const isDownloading = downloading === d.id;
        return <motion.button key={d.id} onClick={() => handleDownload(d)} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: i * 0.03
        }} className="w-full flex items-center gap-4 p-4 hover:bg-navy-50/60 transition-colors text-left group relative" data-id="element-508" data-mp="lgl6epvc4j7c" data-mpi={i}>
                <span className="w-11 h-11 shrink-0 rounded-lg bg-navy-50 flex items-center justify-center text-navy-500 group-hover:text-gold-600 transition-colors" data-id="element-509" data-mp="z7zuhb43nal9">
                  {isKnowledge ? <BookOpen className="w-5 h-5" data-id="element-510" data-mp="3k1368gup6de0" /> : <FileText className="w-5 h-5" data-id="element-511" data-mp="3ezdu0xvqntoc" />}
                </span>
                <div className="flex-1 min-w-0" data-id="element-512" data-mp="17oyoc0o4drkz">
                  <p className="font-semibold text-navy-900 truncate" data-id="element-513" data-mp="17n5j8pt5nff3">
                    {d.title}
                  </p>
                  <p className="text-xs text-navy-400" data-id="element-514" data-mp="xraure292q4e">
                    {d.category} · {d.owner} · Updated {d.updated}
                  </p>
                </div>
                <span className={`hidden sm:inline-block text-[0.65rem] font-bold uppercase tracking-wide px-2 py-1 rounded ${typeStyles[d.type] || 'bg-navy-100 text-navy-700'}`} data-id="element-515" data-mp="12qf6abcnczxw">
                  {d.type}
                </span>
                {!isKnowledge && <span className="hidden md:block text-xs text-navy-400 w-16 text-right" data-id="element-516" data-mp="1gjmvjwd99jcf">
                    {d.size}
                  </span>}
                <span className="p-2 text-navy-400 group-hover:text-gold-600 transition-colors" data-id="element-517" data-mp="51bzh0xgpwra">
                  {isDownloading ? <Check className="w-4 h-4 text-sage" data-id="element-518" data-mp="9zdijluw1w2r" /> : <Download className="w-4 h-4" data-id="element-519" data-mp="2o7o7nxsbq99b" />}
                </span>
                <AnimatePresence data-id="element-520" data-mp="2j9molcuwe9xu">
                  {isDownloading && <motion.span initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} className="absolute right-14 bg-navy-900 text-white text-xs px-2 py-1 rounded shadow-sm whitespace-nowrap" data-id="element-521" data-mp="2xa78ydggodtg">
                      Downloading...
                    </motion.span>}
                </AnimatePresence>
              </motion.button>;
      })}
        </div>}
    </>;
}